import { CurrencyCode, Lang, Locale } from '@revolugo/common/constants'
import {
  Event,
  type RevolugoConfig,
  RevolugoElementsPlugin,
} from '@revolugo/elements-vue3'

import { getPosthogClient } from './01.analytics.client.ts'

import type { Plugin } from 'vue'

const toElementLocale = (locale: Lang | Locale): Locale => {
  switch (locale) {
    case Lang.EN:
    case Locale.en_US: {
      return Locale.en_US
    }
    case Lang.FR:
    case Locale.fr_FR: {
      return Locale.fr_FR
    }
    case Lang.DE:
    case Locale.de_DE: {
      return Locale.de_DE
    }
    case Lang.ES:
    case Locale.es_ES: {
      return Locale.es_ES
    }
    case Lang.IT:
    case Locale.it_IT: {
      return Locale.it_IT
    }
    case Lang.NL:
    case Locale.nl_NL: {
      return Locale.nl_NL
    }
    case Lang.PT:
    case Locale.pt_PT: {
      return Locale.pt_PT
    }
    default: {
      return Locale.en_US
    }
  }
}

const toNuxti18nLocale = (locale: Locale): Lang => {
  switch (locale) {
    case Locale.en_US: {
      return Lang.EN
    }
    case Locale.fr_FR: {
      return Lang.FR
    }
    case Locale.de_DE: {
      return Lang.DE
    }
    case Locale.es_ES: {
      return Lang.ES
    }
    case Locale.it_IT: {
      return Lang.IT
    }
    case Locale.nl_NL: {
      return Lang.NL
    }
    case Locale.pt_PT: {
      return Lang.PT
    }
    default: {
      return Lang.EN
    }
  }
}

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()
  const { eventBus } = useEventBus()
  const route = useRoute()
  const router = useRouter()

  const optionalHeaders: Record<string, string> = {}
  if (config.public.ELEMENTS_CONSUMER_CUSTOM_ID) {
    optionalHeaders['x-consumer-custom-id'] =
      config.public.ELEMENTS_CONSUMER_CUSTOM_ID
  }
  if (config.public.ELEMENTS_GROUPS) {
    optionalHeaders['x-consumer-groups'] = config.public.ELEMENTS_GROUPS
  }

  const posthogClient = getPosthogClient()

  function initElements() {
    const locales =
      posthogClient?.getFeatureFlagPayload('feature--locales') || undefined

    const elementsConfig: Partial<RevolugoConfig> = {
      theme: DEFAULT_THEME,
      ...window?.RevolugoElements?.config,
    }

    nuxtApp.vueApp.use(RevolugoElementsPlugin as Plugin, {
      apiEnvironment: config.public.BOOKING_API_ENVIRONMENT,
      apiKey: config.public.PUBLISHABLE_API_KEY,
      currency: route.query.currency || CurrencyCode.EUR,
      installComponents: false,
      lang: toElementLocale(
        (nuxtApp.$i18n as { locale: Ref<Lang | Locale> }).locale.value,
      ),
      locales,
      optionalHeaders,
      theme: DEFAULT_THEME,
      ...elementsConfig,
    })
  }

  if (posthogClient) {
    let elementsInitialized = false

    const timeout = setTimeout(() => {
      if (!elementsInitialized) {
        elementsInitialized = true
        initElements()
      }
    }, 1000)
    posthogClient.onFeatureFlags(() => {
      clearTimeout(timeout)
      if (!elementsInitialized) {
        elementsInitialized = true
        initElements()
      }
    })
  } else {
    initElements()
  }

  eventBus.value.$on(Event.CurrencyUpdated, newCurrency => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({
      query: {
        ...route.query,
        currency: newCurrency,
      },
    })
  })

  eventBus.value.$on(Event.LangUpdated, newLang => {
    ;(nuxtApp.$i18n as { setLocale: (locale: string) => void }).setLocale(
      toNuxti18nLocale(newLang),
    )
  })
})
